import type { ButtonV3Props } from '@jouzen/ecom-components';
import { usePathname } from 'next/navigation';

import { EventType } from '@/analytics/types';
import { LOW_STOCK } from '@/app/[locale]/product/_consts';
import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import useAddToCart from '@/app/[locale]/product/_hooks/useAddToCart';
import {
  _getIsAddToCart,
  _getIsFinishNext,
  _getIsNotifyMe,
  _getIsSizeNext,
} from '@/app/[locale]/product/_steps/utils/getStepCTA';

import WizStepId from '../enums/WizStepId';

const nextStepDict = {
  [WizStepId.Accessory]: WizStepId.Size,
  [WizStepId.Finish]: WizStepId.Size,
  [WizStepId.Style]: WizStepId.Finish,
};

export interface UseStepReturn {
  readonly buttonProps: Partial<ButtonV3Props>;
  readonly ctaMsgKey: string;
  readonly isLink: boolean;
}

export const useStepCTA = (): UseStepReturn => {
  const wizardContext = useWizardStateContext();
  const {
    currentStep,
    isAccessory,
    isOuraRing4Wizard,
    stockStatus,
    setNotifyModal,
  } = wizardContext;
  const path = usePathname();

  const { onAddToCart } = useAddToCart({
    ...wizardContext,
    stockStatus,
  });

  const handleNextStep = async (CTA: string) => {
    const stockStatusTrack =
      stockStatus === LOW_STOCK ? stockStatus : undefined;
    const nextStep = nextStepDict[currentStep as keyof typeof nextStepDict];

    await window.ouraAnalytics.track(EventType.CTAClicked, {
      action: `go to ${nextStep}`,
      cta: CTA,
      location: 'wizard',
      path,
      stockStatus: stockStatusTrack,
    });
  };

  //Choose your Size CTA
  const sizeNextProps = {
    ctaMsgKey: 'pdp_step_size_next',
    buttonProps: {
      'data-cy': 'pdp_step_size_next',
      loadingTextEnabled: true,
      onClick: () => handleNextStep('choose your size'),
    },
    isLink: true,
  };

  //Choose your Finish CTA
  const finishNextProps = {
    ...sizeNextProps,
    ctaMsgKey: 'pdp_step_style_next',
    buttonProps: {
      'data-cy': 'pdp_step_style_next',
      onClick: () => handleNextStep('choose your finish'),
    },
    isLink: true,
  };

  //Sold Out - Notify Me CTA
  const notifyMeProps = {
    ctaMsgKey: 'inventory_notify_module_button',
    buttonProps: {
      'data-cy': 'button-notify-me',
      onClick: () => {
        setNotifyModal(true);
      },
    },
    isLink: false,
  };

  //Add to Cart CTA
  const addToCartProps = {
    ctaMsgKey: 'pdp_add_to_cart',
    buttonProps: {
      'data-cy': 'button-add-to-cart',
      loadingTextEnabled: true,
      onClick: onAddToCart,
    },
    isLink: false,
  };

  const isNotifyMe = _getIsNotifyMe({ stockStatus, isOuraRing4Wizard });
  const isSizeNext = _getIsSizeNext({ isAccessory, currentStep });
  const isFinishNext = _getIsFinishNext({ isAccessory, currentStep });
  const isAddToCart = _getIsAddToCart({
    isAccessory,
    currentStep,
    stockStatus,
    isOuraRing4Wizard,
  });

  if (isFinishNext) {
    return finishNextProps;
  } else if (isSizeNext) {
    return sizeNextProps;
  } else if (isNotifyMe) {
    return notifyMeProps;
  } else if (isAddToCart) {
    return addToCartProps;
  } else {
    //this is the default return case just in case 'Next' CTA
    return {
      ctaMsgKey: 'next',
      buttonProps: {
        'data-cy': 'choose-next',
      },
      isLink: true,
    };
  }
};
